import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {NucleotideType} from 'biojs/Util';
import ChemicalStructureSVG from 'chemistry/ChemicalStructureSVG';
import DataConnector from 'connect/DataConnector';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {MonomerVisualSymbol, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {DEFAULT_PATTERN_HIGHLIGHT_FILL} from 'modbio/bulk-edit-mods/ModPatternConstants';
import {
  ModifiedNucleotidesLegendNetworkQuery,
  ModifiedNucleotidesLegendNetworkQueryVariables,
} from 'molbio/monomers/__generated/ModifiedNucleotidesLegendNetworkQuery';
import {MonomerGraphQL} from 'molbio/monomers/__generated/MonomerGraphQL';
import {MODIFIED_NUCLEOTIDES_QUERY} from 'molbio/monomers/NucleotidesTab';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {solidLightGrayBorder} from 'styles/Utilities';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE, GRAY, GRAY_DARK, NEAR_BLACK, WHITE} from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';

import {
  ModifiedNucleotidesLegendQuery,
  ModifiedNucleotidesLegendQueryVariables,
} from './__generated/ModifiedNucleotidesLegendQuery';
import {
  ModifiedNucleotidesQuery,
  ModifiedNucleotidesQueryVariables,
} from './__generated/ModifiedNucleotidesQuery';
import {MonomerQuery} from './__generated/MonomerQuery';
import {displayAsNatural, getDisplayModsForSlice} from './ModificationVisualizationUtil';
import ModifiedBase from './ModifiedBase';
import ModifiedPhosphate, {
  DEFAULT_MODIFIED_PHOSPHATE_SYMBOL,
  NULL_PHOSPHATE_SYMBOL,
} from './ModifiedPhosphate';
import ModifiedSugar from './ModifiedSugar';
import {
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  MONOMER_QUERY,
  NucleotideComponent,
} from './MonomerConstants';
import {BasesAndModifiedNucleotides} from './NucleotidesFindAndReplaceModal';
import {hoverMonomers} from './NucleotidesUIReduxActions';

type MonomerChemicalStructureImage = MonomerQuery['monomers'][number]['chemicalStructureImage'];
type ComponentSequenceModifiedNucleotides = NonNullable<
  ModifiedNucleotidesQuery['componentSequence']
>['modifiedNucleotides'][number];

const SVG_LEGEND_NUMBER_COLUMNS = 2;
const SVG_ROW_HEIGHT = 28;
const SVG_WIDTH = 750;
const SVG_PADDING = 10;
const MONOMER_CHEMICAL_STRUCTURE_IMAGE_HEIGHT = 100;
const MONOMER_CHEMICAL_STRUCTURE_IMAGE_MAX_WIDTH = 150;
const MONOMER_CHEMICAL_STRUCTURE_IMAGE_PADDING = 8;

const NUCLEOTIDE_COMPONENT_TO_SVG = {
  [NucleotideComponent.SUGAR]: (color: string | null, _: MonomerVisualSymbol | null): JSX.Element => (
    <ModifiedSugar cx={8} cy={8} radius={7} stroke={color!} />
  ),
  [NucleotideComponent.BASE]: (color: string | null, _: MonomerVisualSymbol | null): JSX.Element => (
    <ModifiedBase cx={8} cy={8} radius={7} fill={color!} />
  ),
  [NucleotideComponent.PHOSPHATE]: (
    _: string | null,
    visualSymbol: MonomerVisualSymbol | null
  ): JSX.Element => <ModifiedPhosphate size={10} offsetX={3} offsetY={4} symbol={visualSymbol!} />,
};

const NUCLEOTIDE_COMPONENT_TO_TEXT = {
  [NucleotideComponent.SUGAR]: 'sugar',
  [NucleotideComponent.BASE]: 'base',
  [NucleotideComponent.PHOSPHATE]: "3' phosphate",
};

const NUCLEOTIDE_COMPONENT_TO_COLOR = {
  [NucleotideComponent.BASE]: DEFAULT_MODIFIED_BASE_COLOR,
  [NucleotideComponent.SUGAR]: DEFAULT_MODIFIED_SUGAR_COLOR,
  [NucleotideComponent.PHOSPHATE]: null,
};

const NULL_PHOSPHATE_KEY = 'NULL_PHOSPHATE_KEY';

export const MODIFIED_NUCLEOTIDES_LEGEND_QUERY = gql`
  # componentSequence does not query id
  # eslint-disable benchlint/id-field-required
  query ModifiedNucleotidesLegendQuery($sequenceId: StrictString!) @engComponent(name: MODIFIED_BIOLOGICS) {
    componentSequence(id: $sequenceId) @client {
      bases @client
    }
  }
`;

export const MODIFIED_NUCLEOTIDES_LEGEND_NETWORK_QUERY = gql`
  query ModifiedNucleotidesLegendNetworkQuery($sequenceId: StrictString!)
  @skipAudit
  @engComponent(name: MODIFIED_BIOLOGICS) {
    sequence(id: $sequenceId) {
      ... on SequenceNode {
        id
        bases
        nucleotideType
        sequenceType
        modifiedNucleotides {
          baseMonomerId
          sugarMonomerId
          phosphateMonomerId
          position
        }
      }
    }
  }
`;

interface EnhancedProps {
  onUpdateHoverState: (hoveredMonomerIds: Array<string | null>) => void;
}

class ModifiedNucleotidesLegendConnector extends DataConnector<
  ModifiedNucleotidesLegendProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    return {
      onUpdateHoverState: (hoveredMonomerIds: Array<string | null>) => {
        this.dispatch(hoverMonomers(this.props.sequenceId, hoveredMonomerIds));
      },
    };
  }
}

interface LegendComponentProps {
  displayName: string;
  color: string | null;
  visualSymbol: MonomerVisualSymbol | null;
  nucleotideComponent: NucleotideComponent;
  isArchived?: boolean;
}

interface InteractiveLegendComponentProps extends LegendComponentProps {
  onUpdateHoverState: (hoveredMonomerIds: Array<string | null>) => void;
  chemicalStructureImage: MonomerChemicalStructureImage | null;
  monomerIds: Set<string | null>;
}

interface LegendComponentSvgProps extends LegendComponentProps {
  row: number;
  col: number;
}

export function LegendComponent({
  displayName,
  color,
  visualSymbol,
  chemicalStructureImage,
  monomerIds,
  nucleotideComponent,
  onUpdateHoverState,
  isArchived,
}: InteractiveLegendComponentProps): JSX.Element {
  const innerComponent = (
    <p
      className={css(styles.component)}
      {...getTestProps({component: 'ModifiedNucleotidesLegend', element: 'legendItem', key: displayName})}
      onMouseEnter={() => {
        onUpdateHoverState([...monomerIds]);
      }}
      onMouseLeave={() => {
        onUpdateHoverState([]);
      }}
      data-amp-safe={false}
    >
      <svg className={css(styles.svg)}>
        {NUCLEOTIDE_COMPONENT_TO_SVG[nucleotideComponent](color, visualSymbol)}
      </svg>
      <span className={css(styles.label, isArchived && styles.archivedLabel)} title={displayName}>
        {displayName}
      </span>
    </p>
  );

  if (chemicalStructureImage == null) {
    return innerComponent;
  } else {
    return (
      <Popover
        renderContent={() => (
          <ChemicalStructureSVG
            chemicalStructureImage={chemicalStructureImage}
            className={css(styles.chemicalStructureContainer)}
            svgClassName={css(styles.chemicalStructureSvg)}
          />
        )}
      >
        {innerComponent}
      </Popover>
    );
  }
}

interface HighlightLegendComponentProps {
  title: string;
  showAxisHighlight?: boolean;
  showBaseHighlight?: boolean;
}

function HighlightLegendComponent({
  title,
  showAxisHighlight,
  showBaseHighlight,
}: HighlightLegendComponentProps): JSX.Element {
  return (
    <p className={css(styles.component)}>
      <svg className={css(styles.svg)}>
        {showBaseHighlight && (
          <rect
            height={16}
            width={16}
            fill={DEFAULT_PATTERN_HIGHLIGHT_FILL}
            shapeRendering="geometricPrecision"
          />
        )}

        {showAxisHighlight && (
          <rect
            height={4}
            width={16}
            fill={DEFAULT_MODIFIED_SUGAR_COLOR}
            y={12}
            shapeRendering="geometricPrecision"
          />
        )}
      </svg>
      <span className={css(styles.label)} title={title}>
        {title}
      </span>
    </p>
  );
}

const LegendComponentSvg = ({
  displayName,
  color,
  visualSymbol,
  nucleotideComponent,
  row,
  col,
}: LegendComponentSvgProps): JSX.Element => (
  <g transform={`translate(${350 * col + SVG_PADDING}, ${SVG_ROW_HEIGHT * row + SVG_PADDING})`}>
    {NUCLEOTIDE_COMPONENT_TO_SVG[nucleotideComponent](color, visualSymbol)}
    <text transform="translate(20, 12)" fill={NEAR_BLACK}>
      {displayName}
    </text>
  </g>
);

interface ModifiedNucleotidesLegendProps {
  sequenceId: string;
  svgOnly?: boolean;
  useNetworkQuery?: boolean;
  baseHighlightComponentTitle?: string;
  axisHighlightComponentTitle?: string;
  showBaseHighlight?: boolean;
  displayModsOverride?: BasesAndModifiedNucleotides | null;
}

export interface LegendItem {
  monomerIds: Set<string | null>;
  displayName: string;
  nucleotideComponent: NucleotideComponent;
  color: string | null;
  visualSymbol: MonomerVisualSymbol | null;
  chemicalStructureImage: MonomerChemicalStructureImage | null;
  key: string;
  isArchived?: boolean;
}

export function createLegendItems(
  modifiedNucleotides: Array<ModifiedNucleotideAttributes>,
  monomers: Array<MonomerGraphQL>,
  nucleotideType: NucleotideType,
  isOligo: boolean
): Array<LegendItem> {
  const seenCustomMonomers: Set<string> = new Set();
  // Monomers that have custom visuals will be shown as separate legend items alphabetically by category
  const nucleotideComponentToCustomMonomers: {[type: string]: Array<LegendItem>} = {
    [NucleotideComponent.BASE]: [],
    [NucleotideComponent.SUGAR]: [],
    [NucleotideComponent.PHOSPHATE]: [],
  };
  // Monomers without custom visuals will be grouped together as one "Other" legend item by category
  const nucleotideComponentToOtherMonomers: {[type: string]: Set<string | null>} = {
    [NucleotideComponent.BASE]: new Set(),
    [NucleotideComponent.SUGAR]: new Set(),
    [NucleotideComponent.PHOSPHATE]: new Set(),
  };

  let hasNullPhosphate = false;

  modifiedNucleotides.forEach((mod) => {
    const baseMonomer = {
      monomer: monomers.find((monomer) => monomer.id === mod.baseMonomerId),
      type: NucleotideComponent.BASE,
      id: mod.baseMonomerId,
    };
    const sugarMonomer = {
      monomer: monomers.find((monomer) => monomer.id === mod.sugarMonomerId),
      type: NucleotideComponent.SUGAR,
      id: mod.sugarMonomerId,
    };
    const phosphateMonomer = {
      monomer: monomers.find((monomer) => monomer.id === mod.phosphateMonomerId),
      type: NucleotideComponent.PHOSPHATE,
      id: mod.phosphateMonomerId,
    };

    // Track null phosphates separately since we use a different visual symbol
    if (!mod.phosphateMonomerId) {
      hasNullPhosphate = true;
    }

    [baseMonomer, sugarMonomer, phosphateMonomer]
      // Degenerate bases are always natural since they don't support modifications
      .filter(({monomer, id}) => id && !displayAsNatural(id, monomers, nucleotideType, isOligo))
      .forEach(({monomer, type, id}) => {
        if (
          (type === NucleotideComponent.BASE &&
            (!monomer?.color || monomer?.color.toUpperCase() === DEFAULT_MODIFIED_BASE_COLOR)) ||
          (type === NucleotideComponent.SUGAR &&
            (!monomer?.color || monomer?.color.toUpperCase() === DEFAULT_MODIFIED_SUGAR_COLOR)) ||
          (type === NucleotideComponent.PHOSPHATE &&
            (!monomer?.visualSymbol || monomer?.visualSymbol === DEFAULT_MODIFIED_PHOSPHATE_SYMBOL))
        ) {
          nucleotideComponentToOtherMonomers[type].add(id);
        } else if (monomer && !seenCustomMonomers.has(id!)) {
          const archivePurpose = monomer.archiveRecord?.purpose;
          nucleotideComponentToCustomMonomers[type].push({
            monomerIds: new Set([id!]),
            displayName: archivePurpose ? `${monomer.name} (${archivePurpose})` : monomer.name,
            nucleotideComponent: type,
            color: monomer.color,
            visualSymbol: monomer.visualSymbol,
            key: id!,
            chemicalStructureImage: monomer.chemicalStructureImage,
            isArchived: Boolean(archivePurpose),
          });
          seenCustomMonomers.add(id!);
        }
      });
  });

  // Legend items are displayed by category (base, sugar, phosphate) then alphabetically within categories
  let allLegendItems: Array<LegendItem> = [];
  [NucleotideComponent.BASE, NucleotideComponent.SUGAR, NucleotideComponent.PHOSPHATE].forEach(
    (nucleotideComponent) => {
      allLegendItems = allLegendItems.concat(
        naturalSortByKey(nucleotideComponentToCustomMonomers[nucleotideComponent], 'displayName')
      );
      if (nucleotideComponentToOtherMonomers[nucleotideComponent].size > 0) {
        allLegendItems.push({
          monomerIds: nucleotideComponentToOtherMonomers[nucleotideComponent],
          displayName:
            nucleotideComponentToCustomMonomers[nucleotideComponent].length === 0
              ? `Modified ${NUCLEOTIDE_COMPONENT_TO_TEXT[nucleotideComponent]}`
              : `Other modified ${NUCLEOTIDE_COMPONENT_TO_TEXT[nucleotideComponent]}s`,
          nucleotideComponent,
          color: NUCLEOTIDE_COMPONENT_TO_COLOR[nucleotideComponent],
          visualSymbol:
            nucleotideComponent === NucleotideComponent.PHOSPHATE ? DEFAULT_MODIFIED_PHOSPHATE_SYMBOL : null,
          key: NUCLEOTIDE_COMPONENT_TO_TEXT[nucleotideComponent],
          chemicalStructureImage: null,
        });
      }
    }
  );

  if (hasNullPhosphate) {
    allLegendItems.push({
      monomerIds: new Set([null]),
      displayName: `Empty ${NUCLEOTIDE_COMPONENT_TO_TEXT[NucleotideComponent.PHOSPHATE]}`,
      nucleotideComponent: NucleotideComponent.PHOSPHATE,
      color: NUCLEOTIDE_COMPONENT_TO_COLOR[NucleotideComponent.PHOSPHATE],
      visualSymbol: NULL_PHOSPHATE_SYMBOL,
      key: NULL_PHOSPHATE_KEY,
      chemicalStructureImage: null,
    });
  }

  return allLegendItems;
}

interface BaseModifiedNucleotidesLegendContentsFromItemsProps {
  legendItems: Array<LegendItem>;
  sequenceId?: string;
}

type ModifiedNucleotidesLegendContentsFromItemsProps = BaseModifiedNucleotidesLegendContentsFromItemsProps &
  Omit<ModifiedNucleotidesLegendProps, 'sequenceId'> &
  EnhancedProps;

export function ModifiedNucleotidesLegendContentsFromItems({
  legendItems,
  onUpdateHoverState,
  svgOnly,
  baseHighlightComponentTitle,
  axisHighlightComponentTitle,
  showBaseHighlight,
}: ModifiedNucleotidesLegendContentsFromItemsProps): JSX.Element | null {
  const [isExpanded, setIsExpanded] = useState(svgOnly);

  function legendIsOverflowing(legendItems: Array<LegendItem>): boolean {
    const legendItemsElement = document.querySelector('.legendItems');
    if (legendItemsElement) {
      const collapsedHeightLimit = (legendItemsElement.querySelector('p')?.clientHeight ?? 0) * 2;
      return legendItemsElement.scrollHeight >= collapsedHeightLimit;
    }
    return legendItems.length > 3;
  }

  function toggleIsExpanded(): void {
    const legendElement = document.querySelector('.modifiedNucleotidesLegend');
    if (isExpanded && legendElement) {
      legendElement.scrollTop = 0;
    }
    setIsExpanded(!isExpanded);
  }

  if (legendItems.length === 0) {
    return null;
  }
  if (svgOnly) {
    const height = Math.ceil(legendItems.length / SVG_LEGEND_NUMBER_COLUMNS) * SVG_ROW_HEIGHT + SVG_PADDING;
    return (
      <svg
        className="modifiedNucleotidesLegend"
        height={height}
        {...getTestProps({component: 'ModifiedNucleotidesLegend', element: 'svgOnlyRoot'})}
      >
        <rect width={SVG_WIDTH} height={height} fill="none" stroke={GRAY} rx="4" />
        {legendItems.map(({key, displayName, nucleotideComponent, color, visualSymbol}, index) => (
          <LegendComponentSvg
            key={key}
            displayName={displayName}
            color={color}
            visualSymbol={visualSymbol}
            nucleotideComponent={nucleotideComponent}
            row={Math.floor(index / SVG_LEGEND_NUMBER_COLUMNS)}
            col={index % SVG_LEGEND_NUMBER_COLUMNS}
          />
        ))}
      </svg>
    );
  }

  const canExpand = legendIsOverflowing(legendItems);
  return (
    <div
      className={classNames('modifiedNucleotidesLegend', css(styles.container), {
        [css(styles.collapsedContainer)]: !isExpanded,
      })}
      {...getTestProps({component: 'ModifiedNucleotidesLegend', element: 'content'})}
    >
      <div
        className={classNames('legendItems', {
          [css(styles.collapsedLegendItems)]: !isExpanded && canExpand,
        })}
      >
        {baseHighlightComponentTitle != null && (
          <HighlightLegendComponent
            title={baseHighlightComponentTitle}
            showBaseHighlight={showBaseHighlight}
          />
        )}
        {axisHighlightComponentTitle != null && (
          <HighlightLegendComponent
            title={axisHighlightComponentTitle}
            showAxisHighlight={true}
            showBaseHighlight={showBaseHighlight}
          />
        )}
        {legendItems.map(
          ({
            key,
            displayName,
            color,
            visualSymbol,
            chemicalStructureImage,
            nucleotideComponent,
            monomerIds,
            isArchived,
          }) => (
            <LegendComponent
              key={key}
              displayName={displayName}
              color={color}
              visualSymbol={visualSymbol}
              chemicalStructureImage={chemicalStructureImage}
              nucleotideComponent={nucleotideComponent}
              onUpdateHoverState={onUpdateHoverState}
              monomerIds={monomerIds}
              isArchived={isArchived}
            />
          )
        )}
        {isExpanded && canExpand && (
          <Button
            buttonStyle={ButtonStyle.SECONDARY_WHITE}
            size={ButtonSize.MINI}
            className={css(styles.expandButton)}
            text="View less"
            onClick={toggleIsExpanded}
          />
        )}
      </div>
      {!isExpanded && canExpand && (
        <Button
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          size={ButtonSize.MINI}
          className={css(styles.expandButton)}
          text="View more"
          onClick={toggleIsExpanded}
        />
      )}
    </div>
  );
}

interface BaseModifiedNucleotidesLegendContentsProps {
  seqMods: Array<ComponentSequenceModifiedNucleotides> | Array<ModifiedNucleotideAttributes>;
  bases: string;
  nucleotideType: NucleotideType;
  sequenceType: SequenceType;
  legendItemsOverride?: Array<LegendItem>;
  sequenceId?: string;
}

type ModifiedNucleotidesLegendContentsProps = BaseModifiedNucleotidesLegendContentsProps &
  Omit<ModifiedNucleotidesLegendProps, 'sequenceId'> &
  EnhancedProps;

export const ModifiedNucleotidesLegendContents = ({
  onUpdateHoverState,
  svgOnly,
  seqMods,
  bases,
  nucleotideType,
  sequenceType,
  baseHighlightComponentTitle,
  axisHighlightComponentTitle,
  showBaseHighlight,
  legendItemsOverride,
  sequenceId,
}: ModifiedNucleotidesLegendContentsProps): JSX.Element => (
  <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={({refetch}) => null}>
    {({monomers}) => {
      const displayMods = getDisplayModsForSlice(monomers, nucleotideType, bases, seqMods, 0);
      const legendItems =
        legendItemsOverride ??
        createLegendItems(displayMods, monomers, nucleotideType, sequenceType === SequenceType.oligo);

      return (
        <ModifiedNucleotidesLegendContentsFromItems
          sequenceId={sequenceId}
          legendItems={legendItems}
          svgOnly={svgOnly}
          baseHighlightComponentTitle={baseHighlightComponentTitle}
          axisHighlightComponentTitle={axisHighlightComponentTitle}
          showBaseHighlight={showBaseHighlight}
          onUpdateHoverState={onUpdateHoverState}
        />
      );
    }}
  </BenchlingQuery>
);

const ModifiedNucleotidesLegend = ({
  onUpdateHoverState,
  sequenceId,
  svgOnly = false,
  useNetworkQuery = false,
  baseHighlightComponentTitle,
  axisHighlightComponentTitle,
  showBaseHighlight = true,
  displayModsOverride,
}: ModifiedNucleotidesLegendProps & EnhancedProps): JSX.Element =>
  useNetworkQuery ? (
    <BenchlingQuery<ModifiedNucleotidesLegendNetworkQuery, ModifiedNucleotidesLegendNetworkQueryVariables>
      query={MODIFIED_NUCLEOTIDES_LEGEND_NETWORK_QUERY}
      variables={{sequenceId}}
      renderLoading={({refetch}) => null}
    >
      {({sequence}) => {
        if (sequence.__typename === 'InaccessibleInfoNode') {
          return null;
        }
        return (
          <ModifiedNucleotidesLegendContents
            seqMods={displayModsOverride?.modifiedNucleotides || sequence.modifiedNucleotides}
            nucleotideType={sequence.nucleotideType}
            bases={displayModsOverride?.bases || sequence.bases}
            sequenceType={sequence.sequenceType}
            svgOnly={svgOnly}
            sequenceId={sequenceId}
            onUpdateHoverState={onUpdateHoverState}
            baseHighlightComponentTitle={baseHighlightComponentTitle}
            axisHighlightComponentTitle={axisHighlightComponentTitle}
            showBaseHighlight={showBaseHighlight}
          />
        );
      }}
    </BenchlingQuery>
  ) : (
    <BenchlingQuery<ModifiedNucleotidesLegendQuery, ModifiedNucleotidesLegendQueryVariables>
      query={MODIFIED_NUCLEOTIDES_LEGEND_QUERY}
      variables={{sequenceId}}
      renderLoading={({refetch}) => null}
    >
      {({componentSequence}) => (
        <BenchlingQuery<ModifiedNucleotidesQuery, ModifiedNucleotidesQueryVariables>
          query={MODIFIED_NUCLEOTIDES_QUERY}
          renderLoading={({refetch}) => null}
          variables={{sequenceId}}
          fetchPolicy="cache-only"
        >
          {(data) => {
            const seqMods = data.componentSequence!.modifiedNucleotides ?? [];
            const nucleotideType = data.componentSequence!.nucleotideType;
            const bases = componentSequence?.bases || '';
            return (
              <ModifiedNucleotidesLegendContents
                seqMods={seqMods}
                nucleotideType={nucleotideType}
                bases={bases}
                sequenceType={data.componentSequence!.type}
                svgOnly={svgOnly}
                sequenceId={sequenceId}
                onUpdateHoverState={onUpdateHoverState}
                showBaseHighlight={showBaseHighlight}
              />
            );
          }}
        </BenchlingQuery>
      )}
    </BenchlingQuery>
  );

const styles = StyleSheet.create({
  chemicalStructureContainer: {
    height: MONOMER_CHEMICAL_STRUCTURE_IMAGE_HEIGHT,
    padding: MONOMER_CHEMICAL_STRUCTURE_IMAGE_PADDING,
  },
  chemicalStructureSvg: {
    height: '100%',
    maxWidth: MONOMER_CHEMICAL_STRUCTURE_IMAGE_MAX_WIDTH,
  },
  component: {
    display: 'inline-flex',
    marginBottom: '10px',
    marginRight: '30px',
    ...FONT_PROXIMA_13,
    ':hover': {
      fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
      cursor: 'default',
    },
  },
  container: {
    ...solidLightGrayBorder,
    borderColor: GRAY,
    width: '100%',
    margin: '20px',
    paddingTop: '10px',
    paddingRight: '10px',
    paddingLeft: '10px',
    maxHeight: '160px',
    overflow: 'auto',
    backgroundColor: WHITE,
  },
  collapsedContainer: {
    maxHeight: '40px',
    overflow: 'hidden',
    display: 'flex',
  },
  collapsedLegendItems: {
    width: 'calc(100% - 100px)',
  },
  expandButton: {
    paddingTop: '0',
    ':hover': {
      background: 'none',
      color: BLUE,
    },
    ':focus': {
      boxShadow: 'none',
    },
  },
  label: {
    textAlign: 'left',
    display: 'inline-block',
    '::before': {
      display: 'block',
      content: 'attr(title)',
      fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
      height: '0',
      overflow: 'hidden',
      visibility: 'hidden',
    },
  },
  archivedLabel: {
    color: GRAY_DARK,
  },
  svg: {
    width: '16px',
    height: '16px',
    margin: '2px 5px 0 0',
  },
});

export default ModifiedNucleotidesLegendConnector.connect(ModifiedNucleotidesLegend);
